import { CellStatus, ColumnType } from "@/queries/smart-tables/types";

/** Column types that can open per-column request-log analytics. */
const SMART_TABLE_REQUEST_LOG_COLUMN_TYPES = new Set<ColumnType>([
  ColumnType.PROMPT_TEMPLATE,
  ColumnType.CONVERSATION_SIMULATOR,
]);

/** Sheet-level Requests analytics requires a Prompt Template column. */
const SMART_TABLE_REQUEST_ANALYTICS_SOURCE_TYPES = new Set<ColumnType>([
  ColumnType.PROMPT_TEMPLATE,
]);

/** Sheet-level Traces analytics requires a Workflow column. */
const SMART_TABLE_TRACE_ANALYTICS_SOURCE_TYPES = new Set<ColumnType>([ColumnType.WORKFLOW]);

/** Cells that have finished (or previously finished) an execution. */
const RAN_CELL_STATUSES = new Set<CellStatus>([
  CellStatus.COMPLETED,
  CellStatus.FAILED,
  CellStatus.STALE,
]);

export const SMART_TABLE_REQUEST_ANALYTICS_DISABLED_TOOLTIP =
  "Add a Prompt Template column to view request analytics.";

export const SMART_TABLE_REQUEST_ANALYTICS_NO_RUNS_TOOLTIP =
  "Run a Prompt Template column to view request analytics.";

export const SMART_TABLE_TRACE_ANALYTICS_DISABLED_TOOLTIP =
  "Add a Workflow column to view trace analytics.";

export const SMART_TABLE_TRACE_ANALYTICS_NO_RUNS_TOOLTIP =
  "Run a Workflow column to view trace analytics.";

export const columnTypeSupportsRequestLogAnalytics = (
  columnType: ColumnType | string | undefined,
): boolean => {
  if (!columnType) {
    return false;
  }

  return SMART_TABLE_REQUEST_LOG_COLUMN_TYPES.has(columnType as ColumnType);
};

export const columnTypeSupportsTraceAnalytics = (
  columnType: ColumnType | string | undefined,
): boolean => {
  if (!columnType) {
    return false;
  }

  return SMART_TABLE_TRACE_ANALYTICS_SOURCE_TYPES.has(columnType as ColumnType);
};

interface AnalyticsSourceColumn {
  id: string;
  type?: ColumnType | string | null;
}

interface AnalyticsSourceCell {
  status?: CellStatus | string | null;
  execution_id?: string | null;
  last_computed_version?: number | null;
}

const getSourceColumnIds = (
  columns: Iterable<AnalyticsSourceColumn>,
  supportedTypes: Set<ColumnType>,
): string[] => {
  const columnIds: string[] = [];

  for (const column of columns) {
    if (column.type && supportedTypes.has(column.type as ColumnType)) {
      columnIds.push(column.id);
    }
  }

  return columnIds;
};

const isRanAnalyticsCell = (cell: AnalyticsSourceCell | undefined): boolean => {
  if (!cell?.status || !RAN_CELL_STATUSES.has(cell.status as CellStatus)) {
    return false;
  }

  // Skip blank scaffolds that were never executed.
  const hasExecutionId =
    cell.execution_id !== null && cell.execution_id !== undefined && cell.execution_id.length > 0;
  const hasComputedVersion =
    cell.last_computed_version !== null && cell.last_computed_version !== undefined;

  return hasExecutionId || hasComputedVersion;
};

const hasRanCellsForColumnIds = (
  columnIds: readonly string[],
  rows: Iterable<ReadonlyMap<string, AnalyticsSourceCell>>,
): boolean => {
  if (columnIds.length === 0) {
    return false;
  }

  const columnIdSet = new Set(columnIds);

  for (const rowCells of rows) {
    for (const columnId of columnIdSet) {
      if (isRanAnalyticsCell(rowCells.get(columnId))) {
        return true;
      }
    }
  }

  return false;
};

export type SmartTableAnalyticsAvailability =
  | { available: true }
  | { available: false; reason: "missing_column" | "no_ran_cells" };

const getAnalyticsAvailability = (
  columns: Iterable<AnalyticsSourceColumn>,
  rows: Iterable<ReadonlyMap<string, AnalyticsSourceCell>>,
  supportedTypes: Set<ColumnType>,
): SmartTableAnalyticsAvailability => {
  const sourceColumnIds = getSourceColumnIds(columns, supportedTypes);
  if (sourceColumnIds.length === 0) {
    return { available: false, reason: "missing_column" };
  }

  if (!hasRanCellsForColumnIds(sourceColumnIds, rows)) {
    return { available: false, reason: "no_ran_cells" };
  }

  return { available: true };
};

export const getRequestAnalyticsAvailability = (
  columns: Iterable<AnalyticsSourceColumn>,
  rows: Iterable<ReadonlyMap<string, AnalyticsSourceCell>>,
): SmartTableAnalyticsAvailability =>
  getAnalyticsAvailability(columns, rows, SMART_TABLE_REQUEST_ANALYTICS_SOURCE_TYPES);

export const getTraceAnalyticsAvailability = (
  columns: Iterable<AnalyticsSourceColumn>,
  rows: Iterable<ReadonlyMap<string, AnalyticsSourceCell>>,
): SmartTableAnalyticsAvailability =>
  getAnalyticsAvailability(columns, rows, SMART_TABLE_TRACE_ANALYTICS_SOURCE_TYPES);

export const getRequestAnalyticsDisabledTooltip = (
  availability: SmartTableAnalyticsAvailability,
): string | undefined => {
  if (availability.available) {
    return undefined;
  }

  return availability.reason === "no_ran_cells"
    ? SMART_TABLE_REQUEST_ANALYTICS_NO_RUNS_TOOLTIP
    : SMART_TABLE_REQUEST_ANALYTICS_DISABLED_TOOLTIP;
};

export const getTraceAnalyticsDisabledTooltip = (
  availability: SmartTableAnalyticsAvailability,
): string | undefined => {
  if (availability.available) {
    return undefined;
  }

  return availability.reason === "no_ran_cells"
    ? SMART_TABLE_TRACE_ANALYTICS_NO_RUNS_TOOLTIP
    : SMART_TABLE_TRACE_ANALYTICS_DISABLED_TOOLTIP;
};
